import { t, useLocale } from "@/shared/i18n/locale";
import { LanguageSelect } from "@/shared/i18n/language-select";
import { Suspense, useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { LogOut, Menu, Moon, Sun, Monitor } from "lucide-react";
import { useSession, useLogout } from "@/features/auth/use-auth";
import { Sidebar } from "@/shared/layout/sidebar";
import { useTheme } from "@/shared/theme/theme-provider";

export function AppShell() {
  useLocale();
  const session = useSession();
  const logout = useLogout();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    const close = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, []);
  const user = session.data;
  async function handleLogout() { await logout.mutateAsync(); navigate("/login", { replace: true }); }
  return <div className="min-h-screen md:flex">
    <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-[var(--card)] focus:p-3">{t("Ir para o conteúdo")}</a>
    <Sidebar open={open} onClose={() => setOpen(false)} collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />
    <div className="min-w-0 flex-1">
      <header className="app-header flex h-20 items-center justify-between gap-4 border-b border-[var(--border)] bg-[var(--card)] px-4 md:px-8">
        <div className="flex items-center gap-3"><button onClick={() => setOpen(true)} aria-label={t("Abrir menu")} aria-expanded={open} className="md:hidden"><Menu size={20} /></button><span className="hidden text-sm text-[var(--muted-foreground)] lg:inline">{t("Workspace")}{" "}<span className="mx-2 opacity-40">/</span> <span className="font-semibold text-[var(--foreground)]">Pingys Admin</span></span></div>
        <div className="flex items-center gap-2 md:gap-4">
          <LanguageSelect />
          <div className="flex rounded-lg border border-[var(--border)] p-1" aria-label={t("Tema")}>{([["light", Sun, t("Claro")], ["dark", Moon, t("Escuro")], ["system", Monitor, t("Automático")]] as const).map(([value, Icon, label]) => <button key={value} title={t(label)} aria-label={t("Tema {0}", { 0: t(label).toLowerCase() })} aria-pressed={theme === value} onClick={() => setTheme(value)} className={`rounded-md p-1.5 ${theme === value ? "bg-[var(--muted)] text-[var(--foreground)]" : "text-[var(--muted-foreground)]"}`}><Icon size={16} /></button>)}</div>
          <div className="hidden text-right sm:block"><p className="text-xs font-bold">{user?.name ?? user?.email}</p><p className="text-[10px] text-[var(--muted-foreground)]">{t("Administrador")}</p></div>
          <button aria-label={t("Sair")} title={t("Sair")} disabled={logout.isPending} onClick={handleLogout} className="text-[var(--muted-foreground)]"><LogOut size={17} /></button>
        </div>
      </header>
      <main id="main-content" className="mx-auto max-w-[1600px] p-4 md:p-8" tabIndex={-1}><Suspense fallback={<div role="status" className="p-10 text-sm text-[var(--muted-foreground)]">{t("Carregando página…")}</div>}><Outlet /></Suspense></main>
    </div>
  </div>;
}
